import { DATASET_COLORS, SHIPPING_ROUTES_PRICES } from '~/constants/chart'
import { MARKET_SLUG } from '~/src/constants/markets'
import type { TGeneral } from '~/src/types/common'

export const getProduct = ({ _product, unitOfMeasure, currency, dataset }) => {
  let product = { ..._product }

  // change region name only for Lithium and CIF Asia (id 1)
  if (
    product.shippingRoute?.name === SHIPPING_ROUTES_PRICES.cif_asia.name &&
    product.mineral === MARKET_SLUG.LITHIUM
  ) {
    product = {
      ...product,
      shippingRoute: {
        ...product.shippingRoute,
        name: SHIPPING_ROUTES_PRICES.cif_asia_combined.name,
      },
    }
  }

  const label = getProductLabel({
    ...product,
    unit: unitOfMeasure.symbol,
    market: product.Product?.ProductName?.toLowerCase(),
  })

  return {
    ...product,
    label,
    dataset,
    dashed: product?.priceType?.name === 'Daily Price Indicator',
    symbol:
      unitOfMeasure.symbol === '%' ? unitOfMeasure.symbol : currency.symbol,
    isSustainable: product.category?.isSustainable,
    priceType: product.priceType,
    category: product.category,
    feedstock: product.feedstock,
    hidden: product.hidden,
    hash: product.hash,
    seriesTitle: product.seriesTitle,
    market: product.Product?.ProductName?.toLowerCase(),
  }
}

const getPriceOrder = (name: string) => {
  if (name === 'Daily Price Indicator') return 0

  if (name === 'Global Weighted Average') return 1

  return 2
}

const getShippingRouteLabel = ({
  shippingRoute,
  priceType,
}: {
  shippingRoute: any
  priceType: any
}) => {
  if (!shippingRoute) {
    const priceTypeName = priceType?.name || ''
    return `${priceTypeName};`
  }

  if (priceType.name === 'Daily Price Indicator') {
    return `${priceType.name}; ${shippingRoute.name};`
  }

  return `${shippingRoute.name};`
}

function getCurrency(currency: any, priceType: any, unitOfMeasure: any) {
  if (priceType && priceType.name === 'Payable') {
    return ` ${priceType.name}; %`
  }
  if (currency) {
    return `${currency.iso}/${unitOfMeasure.name}`
  }
  return ''
}

const getProductLabel = ({
  purity,
  format,
  grade,
  shippingRoute,
  meshSize,
  product,
  priceType,
  currency,
  unitOfMeasure,
  market,
  category,
}: TGeneral) => {
  const purityLabel = purity?.name ? `${purity.name};` : ''
  const currencyLabel = getCurrency(currency, priceType, unitOfMeasure)
  const shippingRouteLabel = getShippingRouteLabel({
    priceType,
    shippingRoute,
  })
  const productLabel = product ? `${product.alias || product.name};` : ''
  const formatLabel = format ? `${format.name};` : ''
  const gradeLabel = grade ? `${grade};` : ''
  const meshSizeLabel = meshSize ? `${meshSize.size};` : ''

  let label = [
    shippingRouteLabel,
    purityLabel,
    formatLabel,
    gradeLabel,
    meshSizeLabel,
    productLabel,
    currencyLabel,
  ]
    .join(' ')
    .trim()

  if (market === MARKET_SLUG.CATHODE) {
    const categoryLabel = category ? `${category.name};` : ''
    label = [shippingRouteLabel, categoryLabel, currencyLabel].join(' ').trim()
  }

  if (priceType?.name?.toLowerCase() === 'price daily') {
    const priceTypeName = `${priceType.name};`
    label = [
      shippingRouteLabel,
      purityLabel,
      priceTypeName,
      formatLabel,
      gradeLabel,
      meshSizeLabel,
      productLabel,
      currencyLabel,
    ]
      .join(' ')
      .trim()
  }

  return label.endsWith(';') ? label.slice(0, -1) : label
}

export const getCategoriesByProduct = ({ product, categories }: TGeneral) => {
  const categoryLabel = product.category
    ? product.category.name
    : product.feedstock.name
  if (!categoryLabel) return
  const categoryIndex = categories.findIndex(
    (category) => category.name.trim() === categoryLabel,
  )

  const newPriceType = {
    ...product.priceType,
    value: false,
    order: getPriceOrder(product.priceType.name),
  }

  if (categoryIndex < 0) {
    return [
      ...categories,
      {
        name: categoryLabel,
        priceType: [newPriceType],
        value: false,
        market: product.market,
        seriesTitle: product.seriesTitle,
      },
    ]
  }
  const existingCategory = categories[categoryIndex]
  const hasPriceType = existingCategory.priceType.some(
    (price: any) => price.id === product.priceType.id,
  )

  if (hasPriceType) return categories

  return [
    ...categories.slice(0, categoryIndex),
    existingCategory,
    ...categories.slice(categoryIndex + 1),
  ]
}

export const getMinMax = (dataset) => {
  if (!dataset || dataset.length <= 0) {
    return {
      min: 0,
      max: null,
    }
  }
  const allYValues = dataset
    .flatMap((entry) => entry.dataset.map((dataPoint) => dataPoint?.y))
    .filter((value) => typeof value === 'number') as number[]

  if (allYValues.length <= 0) {
    return {
      min: 0,
      max: null,
    }
  }
  const minNumber = Math.min(...allYValues)
  const maxNumber = Math.max(...allYValues)
  return {
    min: Math.max(0, minNumber - 20),
    max: Math.min(100, maxNumber + 20),
  }
}

export const createDataset = ({
  datasetInfo,
  yAxisID,
  labelPrefix = '',
  digits,
  borderColor,
}: {
  datasetInfo: any
  yAxisID: string
  labelPrefix?: string
  digits?: number
  borderColor?: string
}) => {
  return {
    label: `${labelPrefix}${datasetInfo.label}`,
    data: datasetInfo.dataset.filter(Boolean),
    borderColor: datasetInfo.dashed ? '#ffb152' : borderColor,
    borderDash: datasetInfo.dashed ? [5, 5] : [],
    fill: false,
    yAxisID,
    spanGaps: true,
    digits,
    symbol: datasetInfo.symbol,
    isSustainable: datasetInfo.isSustainable,
    priceType: datasetInfo.priceType,
    category: datasetInfo.category,
    feedstock: datasetInfo.feedstock,
    hidden: datasetInfo.hidden,
    hash: datasetInfo.hash,
    seriesTitle: datasetInfo.seriesTitle,
    market: datasetInfo.market,
  }
}

export const orderDataset = (datasets, order: string[] = []) => {
  return [...datasets].sort((a, b) => {
    const category1 = a.label.split('#')[0].trim()
    const category2 = b.label.split('#')[0].trim()
    const orderComparison = order.indexOf(category1) - order.indexOf(category2)
    if (orderComparison !== 0) return orderComparison

    const [subCategory1, route1] = a.label.split('-').map((x) => x.trim())
    const [subCategory2, route2] = b.label.split('-').map((x) => x.trim())
    if (subCategory1 < subCategory2) return -1
    if (subCategory1 > subCategory2) return 1
    if (route1 < route2) return -1
    if (route1 > route2) return 1
    return 0
  })
}

export const extractDates = (rawData) => {
  const datesSet = new Set<string>()

  for (const _item in rawData) {
    if (Object.hasOwn(rawData, _item)) {
      const dates = rawData[_item]?.data?.flatMap((_productType: any) =>
        _productType.series?.map(({ date }: { date: string }) => date),
      )
      dates?.forEach((date: string) => datesSet.add(date))
    }
  }

  return Array.from(datesSet).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  )
}

export const getValidCategories = (allData, categories) => {
  if (!categories || categories.length === 0) return []
  if (!allData || allData.length === 0) return categories
  return [...categories].map((category) => {
    const name = category?.name

    if (!name) return category

    const value = !allData.some((data) => {
      const nameCategory = data.category?.name || data.feedstock?.name
      return nameCategory === name && data.hidden
    })

    const priceType = [...category.priceType]
      .sort((a, b) => a.order - b.order)
      .map((price) => {
        const value = !allData.some((data) => {
          const nameCategory = data.category?.name || data.feedstock?.name
          return (
            nameCategory === name &&
            data.priceType.name === price.name &&
            data.hidden
          )
        })
        return { ...price, value }
      })

    return { ...category, value, priceType }
  })
}

export const getNewColor = (index: number) =>
  DATASET_COLORS[index % DATASET_COLORS.length]

export const getCategoriesByProducts = (
  categories,
  products,
  unitOfMeasure,
) => {
  if (!products) return categories
  if (!categories) return []
  let newCategories = categories

  products.forEach((product) => {
    newCategories = getCategoriesByProduct({
      product: {
        category: product.category,
        feedstock: product.feedstock,
        priceType: product.priceType,
        seriesTitle: product.seriesTitle,
        unit: unitOfMeasure.symbol,
        market: product.Product?.ProductName?.toLowerCase(),
      },
      categories: newCategories,
    })
  })

  return newCategories
}

export const getProductByRawData = (rawData, dates) => {
  if (rawData === null) return

  const { currency, unitOfMeasure, data } = rawData

  return data
    ?.map((_product: any) => {
      const dataset = dates
        .map((date) => {
          const record = _product.series.find(
            (_series: any) => _series.date === date,
          )
          return record ? { y: record.valueMid, x: record.date } : null
        })
        .filter(Boolean)
      const product = getProduct({ _product, currency, unitOfMeasure, dataset })

      if (product) return product
      return null
    })
    .filter(Boolean)
}
